import { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { useTranslation } from "react-i18next";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";

const normalizeLanguage = (language) =>
  String(language || "")
    .toLowerCase()
    .startsWith("ar")
    ? "ar"
    : "en";

const isAbsoluteUrl = (value) => /^(?:[a-z]+:)?\/\//i.test(value);

const resolveContentAssetPath = (assetBasePath, assetPath) => {
  if (!assetPath) return "";

  if (
    assetPath.startsWith("#") ||
    assetPath.startsWith("/") ||
    assetPath.startsWith("mailto:") ||
    assetPath.startsWith("tel:") ||
    isAbsoluteUrl(assetPath)
  ) {
    return assetPath;
  }

  if (!assetBasePath) {
    return assetPath;
  }

  const normalizedBasePath = assetBasePath.endsWith("/")
    ? assetBasePath
    : `${assetBasePath}/`;

  return `${normalizedBasePath}${assetPath.replace(/^\.\//, "")}`;
};

const joinClassNames = (...values) =>
  Array.from(
    new Set(
      values.flatMap((value) =>
        String(value || "")
          .split(/\s+/)
          .filter(Boolean),
      ),
    ),
  ).join(" ");

const getPageLocale = (page, language) => {
  if (!page?.locales) return null;
  return page.locales[normalizeLanguage(language)] || page.locales.en || null;
};

const MarkdownResourcePage = ({ page }) => {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const locale = useMemo(() => getPageLocale(page, language), [page, language]);

  if (!locale?.content) {
    const message =
      normalizeLanguage(language) === "ar"
        ? "تعذر تحميل هذا الدليل الآن."
        : "This guide could not be loaded right now.";

    return (
      <article className="doc-container resource-markdown-page">
        <p className="doc-text">{message}</p>
      </article>
    );
  }

  return (
    <article className="doc-container markdown-body resource-markdown-page">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          a({ href = "", className, children, ...props }) {
            const resolvedHref = resolveContentAssetPath(
              locale.assetBasePath,
              href,
            );
            const external = isAbsoluteUrl(resolvedHref);

            return (
              <a
                {...props}
                href={resolvedHref}
                className={className}
                target={external ? "_blank" : undefined}
                rel={external ? "noreferrer" : undefined}
              >
                {children}
              </a>
            );
          },
          img({ src = "", alt = "", className, ...props }) {
            return (
              <img
                {...props}
                src={resolveContentAssetPath(locale.assetBasePath, src)}
                alt={alt}
                className={joinClassNames("doc-image", className)}
                loading="lazy"
              />
            );
          },
        }}
      >
        {locale.content}
      </ReactMarkdown>
    </article>
  );
};

export default MarkdownResourcePage;
